import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import WizardStep from '../WizardStep';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Store, Rss, Package, ExternalLink, UserX } from 'lucide-react';
import { useSelection } from '@/contexts/SelectionContext';
import { supabase } from '@/integrations/supabase/client';
import { autoSelectId } from '@/lib/wizardSteps';
import { collectEmbedded } from '@/lib/mcMembership';

interface SelectFeedStepProps {
  googleAccountId: string | null;
  /** Receives the explicitly-chosen feedId so the wizard can thread it to the download step. */
  onNext: (feedId: string) => void;
  onBack?: () => void;
  onFinish: () => void;
}

const GMC_SOURCES_URL = 'https://merchants.google.com/mc/products/sources';
const GMC_HOME_URL = 'https://merchants.google.com/';

/**
 * Wizard step: pick a Merchant Center + feed.
 *
 * The selection is held in LOCAL state (not the global SelectionContext) so the
 * wizard is the single source of truth during onboarding — this avoids the race
 * with SelectionContext.autoSelectDefaults that previously pre-filled / clobbered
 * the feed. The chosen feed is committed to the global selection (atomically) and
 * threaded to the download step only when the user explicitly proceeds.
 *
 * The MC is auto-filled when there is exactly one (pure convenience). The FEED is
 * never auto-selected — the user always picks it consciously.
 */
const SelectFeedStep: React.FC<SelectFeedStepProps> = ({ googleAccountId, onNext, onBack, onFinish }) => {
  const { t } = useTranslation('wizard');
  const { setSelection } = useSelection();

  const [mcId, setMcId] = useState<string | null>(null);
  const [feedId, setFeedId] = useState<string | null>(null);

  const { data: merchantCenters = [], isLoading: mcLoading } = useQuery({
    queryKey: ['wizard-select', 'merchant-centers', googleAccountId],
    queryFn: async () => {
      // Mitgliedschafts-Umstellung (Task 10): "MCs dieses Google-Kontos" laeuft
      // ueber merchant_center_members statt direkt ueber merchant_centers.
      // `!inner` filtert Mitgliedschaftszeilen ohne sichtbare MC weg.
      const { data, error } = await supabase
        .from('merchant_center_members')
        .select('merchant_centers!inner(id, name, merchant_center_id)')
        .eq('google_account_id', googleAccountId);
      if (error) { Sentry.captureException(error, { tags: { feature: 'wizard', action: 'select-feed-merchant-centers' } }); throw error; }

      const rows = data ?? [];
      // Flatten/Dedupe-Kern liegt seit Fix-Runde 1 (Important 1) in
      // `collectEmbedded` (`lib/mcMembership.ts`), statt hier ein fuenftes
      // Mal dieselbe Schleife zu tragen.
      const merchants = collectEmbedded(
        rows,
        (row) => row.merchant_centers,
        (_row, mc) => mc.id,
      ).map(({ embed }) => embed);
      merchants.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      return merchants;
    },
    enabled: !!googleAccountId,
  });

  // Auto-fill the MC only when there is exactly one (convenience). Feed stays manual.
  useEffect(() => {
    if (mcLoading || mcId) return;
    const only = autoSelectId(merchantCenters);
    if (only) setMcId(only);
  }, [mcLoading, merchantCenters, mcId]);

  const { data: feeds = [], isLoading: feedLoading } = useQuery({
    queryKey: ['wizard-select', 'feeds', mcId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feeds')
        .select('id, display_name, feed_id')
        .eq('merchant_center_id', mcId)
        .neq('feed_type', 'supplemental')
        .or('feed_input_type.is.null,feed_input_type.neq.automatic')
        .order('display_name', { ascending: true });
      if (error) { Sentry.captureException(error, { tags: { feature: 'wizard', action: 'select-feed-feeds' } }); throw error; }
      return data || [];
    },
    enabled: !!mcId,
  });

  const noMerchantCenters = !!googleAccountId && !mcLoading && merchantCenters.length === 0;
  const noFeeds = !!mcId && !feedLoading && feeds.length === 0;

  const handleDownload = () => {
    if (!googleAccountId || !mcId || !feedId) return;
    // Commit the conscious choice to the global selection (atomic — no cascading
    // nulls) so the dashboard + optimize-all use exactly this feed, then thread the
    // feedId to the download step so it never depends on async global propagation.
    setSelection(googleAccountId, mcId, feedId);
    onNext(feedId);
  };

  if (noMerchantCenters) {
    return (
      <WizardStep title={t('selectFeed.noMcTitle')} hideNext hideBack>
        <div className="flex flex-col items-center text-center space-y-4 py-2">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <UserX className="w-7 h-7 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">{t('selectFeed.noMcBody')}</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button asChild variant="outline" size="sm">
              <a href={GMC_HOME_URL} target="_blank" rel="noopener noreferrer">
                {t('selectFeed.openGmc')}
                <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
              </a>
            </Button>
            <Button size="sm" onClick={onFinish}>{t('selectFeed.noMcSwitchAccount')}</Button>
          </div>
        </div>
      </WizardStep>
    );
  }

  if (noFeeds) {
    return (
      <WizardStep title={t('selectFeed.noFeedTitle')} hideNext hideBack>
        <div className="flex flex-col items-center text-center space-y-4 py-2">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Package className="w-7 h-7 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">{t('selectFeed.noFeedBody')}</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <Button asChild variant="outline" size="sm">
              <a href={GMC_SOURCES_URL} target="_blank" rel="noopener noreferrer">
                {t('selectFeed.openGmc')}
                <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
              </a>
            </Button>
            <Button size="sm" onClick={onFinish}>{t('selectFeed.finish')}</Button>
          </div>
        </div>
      </WizardStep>
    );
  }

  return (
    <WizardStep
      title={t('selectFeed.title')}
      description={t('selectFeed.description')}
      onBack={onBack}
      hideBack={!onBack}
      onNext={handleDownload}
      nextLabel={t('selectFeed.downloadButton')}
      nextDisabled={!feedId}
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary">
            <Store className="h-3 w-3" /> {t('selectFeed.merchantCenter')}
          </div>
          <Select
            value={mcId || ''}
            onValueChange={(v) => { setMcId(v); setFeedId(null); }}
            disabled={mcLoading || merchantCenters.length === 0}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={t('selectFeed.selectMerchant')} />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {merchantCenters.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name || m.merchant_center_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary">
            <Rss className="h-3 w-3" /> {t('selectFeed.feed')}
          </div>
          <Select
            value={feedId || ''}
            onValueChange={(v) => setFeedId(v)}
            disabled={!mcId || feedLoading}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={t('selectFeed.selectFeed')} />
            </SelectTrigger>
            <SelectContent className="bg-background">
              {feeds.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.display_name || f.feed_id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </WizardStep>
  );
};

export default SelectFeedStep;
